import { createRoute } from "@tanstack/solid-router";

import { rootRoute } from "../__root";
import { sessionActor } from "@/lib/session-machine";
import { accountsStore, readAccountsStateSync, readActiveAccountSync } from "@/lib/accounts";
import { ensureTabAccount } from "@/lib/active-account";
import { api, fetchApi } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import {
  cloneSession,
  type components,
  HTTP_FORBIDDEN,
  HTTP_UNAUTHORIZED,
  isErrorModel,
  isStatusError,
  isValidSession,
  queryKeys,
  type Session,
} from "@malla/shared";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { AppLayoutContent } from "@/features/layout/components/app-layout-content";
import { SESSION_CHECK_TIMEOUT_MS, SESSION_STALE_TIME_MS } from "@/lib/constants";

function AppLayout() {
  const { db } = usePowerSyncContext();

  // The shell mounts immediately; `db` may be null while PowerSync's local DB
  // initializes. AppLayoutContent renders the static chrome instantly and shows
  // granular skeletons per data region until `db` is ready.
  return <AppLayoutContent db={db()} />;
}

// Deduplicates concurrent beforeLoad calls during the page-load preload burst.
// When viewport preloads fire simultaneously for multiple child routes, they all
// share a single API request instead of each sending their own.
let activeSessionCheck: Promise<void> | null = null;

async function runSessionCheck(
  snapshot: ReturnType<typeof sessionActor.getSnapshot>,
): Promise<void> {
  // Wait for the machine to finish priming from storage.
  if (snapshot.matches("priming")) {
    await new Promise<void>((resolve) => {
      const sub = sessionActor.subscribe((s) => {
        if (!s.matches("priming")) {
          sub.unsubscribe();
          resolve();
        }
      });
    });
  }

  // Multi-account: if the user explicitly chose the GUEST account, do NOT verify
  // with the server. The backend cookie may still authenticate a real account,
  // and pushing that into the machine here would flip the app back to that real
  // identity (and swap the active PowerSync DB away from the guest). Honor the
  // explicit guest choice; the real session is re-primed on switching back.
  if (readActiveAccountSync()?.kind === "guest") {
    return;
  }

  const currentSnapshot = sessionActor.getSnapshot();
  // Machine may have recovered a session from localStorage during priming.
  // If we have a session OR we have already verified with the server, we skip.
  if (currentSnapshot.context.session || currentSnapshot.context.isVerified) {
    return;
  }

  // No session in machine — verify with the server once.
  const fetchSession = async () =>
    api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });
  type FetchResult = Awaited<ReturnType<typeof fetchSession>>;

  const sessionResult = await (async () => {
    try {
      const timeoutPromise = new Promise<FetchResult>((_resolve, reject) => {
        setTimeout(() => {
          reject(new Error("Session check timeout"));
        }, SESSION_CHECK_TIMEOUT_MS);
      });

      return await Promise.race([fetchSession(), timeoutPromise]);
    } catch (err) {
      console.warn("[SessionCheck] Error or timeout verifying session:", err);
      return null;
    }
  })();

  if (!sessionResult) {
    return;
  }

  const { data, error, response } = sessionResult;

  // Sync with machine regardless of success/error (error will clear session)
  let sessionPayload: Session | null = null;
  if (isValidSession(data)) {
    sessionPayload = cloneSession(data);
  }

  let errorPayload: components["schemas"]["ErrorModel"] | null = null;
  if (isErrorModel(error)) {
    errorPayload = error;
  }

  sessionActor.send({
    type: "SERVER_REPLY",
    session: sessionPayload,
    error: errorPayload,
  });

  if (error) {
    const status = isStatusError(error) ? error.status : response.status;
    if (status === HTTP_UNAUTHORIZED || status === HTTP_FORBIDDEN) {
      return;
    }
    return;
  }

  queryClient.setQueryData(queryKeys.auth.session(), data);
}

export const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_app",
  validateSearch: (search: Record<string, unknown>): { account?: "switch" } => ({
    account: search.account === "switch" ? "switch" : undefined,
  }),
  beforeLoad: () => {
    const accountsState = readAccountsStateSync();
    if (accountsState.accounts.length === 0) {
      void accountsStore.ensureGuestActive();
    } else {
      ensureTabAccount(accountsState);
    }

    const snapshot = sessionActor.getSnapshot();

    // Hot path: session already established and machine is done priming.
    // We only prefetch if the cached data is stale — useSession's createQuery
    // will handle refetching reactively once mounted.
    if (!snapshot.matches("priming") && snapshot.context.session) {
      const cached = queryClient.getQueryState(queryKeys.auth.session());
      const isStale =
        !cached?.dataUpdatedAt || Date.now() - cached.dataUpdatedAt > SESSION_STALE_TIME_MS;

      if (isStale) {
        void queryClient.prefetchQuery({
          queryKey: queryKeys.auth.session(),
          queryFn: async () =>
            fetchApi(api.GET("/api/auth/get-session", { params: { query: { jwt: true } } })),
          staleTime: SESSION_STALE_TIME_MS,
        });
      }
      return;
    }

    // Cold path: fire the verify in the background and let the route render
    // immediately.
    activeSessionCheck ??= (async () => {
      try {
        await runSessionCheck(snapshot);
      } finally {
        activeSessionCheck = null;
      }
    })();
  },

  component: AppLayout,
});
